import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import BrandedLoader from "@/components/BrandedLoader";
import BackNavigation from "@/components/BackNavigation";

// ---------------------------------------------------------------
// Route-level code splitting — every page below is only fetched
// when the user actually navigates to it. This cuts the initial
// JS payload from a single ~2MB bundle to a lightweight shell +
// the current route's chunk, so the branded loader flashes for
// well under a second on 4G instead of blocking the whole app.
// The dashboard is imported eagerly because it's the most common
// post-login landing and we don't want an extra network hop for
// the first thing a returning user sees.
// ---------------------------------------------------------------
import Dashboard from "@/pages/Dashboard";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";

const Register = lazy(() => import("@/pages/Register"));
const Workspace = lazy(() => import("@/pages/Workspace"));
const Admin = lazy(() => import("@/pages/Admin"));
const B2COrders = lazy(() => import("@/pages/B2COrders"));
const B2CLedger = lazy(() => import("@/pages/B2CLedger"));
const AccountingReports = lazy(() => import("@/pages/AccountingReports"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const PublicTrack = lazy(() => import("@/pages/PublicTrack"));
const PublicRate = lazy(() => import("@/pages/PublicRate"));
const Reports = lazy(() => import("@/pages/Reports"));
const Profile = lazy(() => import("@/pages/Profile"));
const Customers = lazy(() => import("@/pages/Customers"));
const Products = lazy(() => import("@/pages/Products"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const AdminPerformance = lazy(() => import("@/pages/admin/AdminPerformance"));
const AdminTransporters = lazy(() => import("@/pages/admin/AdminTransporters"));
const AdminTransporterAgeing = lazy(() => import("@/pages/admin/AdminTransporterAgeing"));
const AdminTripTracker = lazy(() => import("@/pages/admin/AdminTripTracker"));
const AdminWallets = lazy(() => import("@/pages/admin/AdminWallets"));
const MaterialLogs = lazy(() => import("@/pages/MaterialLogs"));
const TeamGuide = lazy(() => import("@/pages/TeamGuide"));
const Orders = lazy(() => import("@/pages/Orders"));
const Chat = lazy(() => import("@/pages/Chat"));
const OrderNew = lazy(() => import("@/pages/OrderNew"));
const OrderDetail = lazy(() => import("@/pages/OrderDetail"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const ExpenseNew = lazy(() => import("@/pages/ExpenseNew"));
const Shop = lazy(() => import("@/pages/Shop"));
const Store = lazy(() => import("@/pages/Store"));
const ChatSupportBridge = lazy(() => import("@/pages/ChatSupportBridge"));
const OrderRequests = lazy(() => import("@/pages/OrderRequests"));
const Team = lazy(() => import("@/pages/Team"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const PurchaseOrders = lazy(() => import("@/pages/PurchaseOrders"));
const PurchaseOrderNew = lazy(() => import("@/pages/PurchaseOrderNew"));
const Files = lazy(() => import("@/pages/Files"));
const MfgDashboard = lazy(() => import("@/pages/mfg/MfgDashboard"));
const MfgMachines = lazy(() => import("@/pages/mfg/MfgMachines"));
const MfgMoulds = lazy(() => import("@/pages/mfg/MfgMoulds"));
const MfgProducts = lazy(() => import("@/pages/mfg/MfgProducts"));
const MfgShifts = lazy(() => import("@/pages/mfg/MfgShifts"));
const MfgProductionEntry = lazy(() => import("@/pages/mfg/MfgProductionEntry"));
const MfgProductionEntries = lazy(() => import("@/pages/mfg/MfgProductionEntries"));
const MfgPlanning = lazy(() => import("@/pages/mfg/MfgPlanning"));
const MfgReports = lazy(() => import("@/pages/mfg/MfgReports"));
const MfgAlerts = lazy(() => import("@/pages/mfg/MfgAlerts"));
const MfgEmployees = lazy(() => import("@/pages/mfg/MfgEmployees"));
const MfgAttendance = lazy(() => import("@/pages/mfg/MfgAttendance"));
const MfgPayroll = lazy(() => import("@/pages/mfg/MfgPayroll"));
const HourlyLog = lazy(() => import("@/pages/mfg/HourlyLog"));
const CrmDashboard = lazy(() => import("@/pages/crm/CrmDashboard"));
const CrmLeads = lazy(() => import("@/pages/crm/CrmLeads"));
const CrmPipeline = lazy(() => import("@/pages/crm/CrmPipeline"));
const CrmActivities = lazy(() => import("@/pages/crm/CrmActivities"));

const ROLES_BIZ = ["admin", "manager", "user"];
const ROLES_LEADS = ["admin", "manager"];    // any role that runs the shop
const ROLES_ADMIN = ["admin"];               // strict — User Settings only

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <BackNavigation />
          <Suspense fallback={<BrandedLoader />}>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/dashboard" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute module="team"><Team /></ProtectedRoute>} />
              <Route path="/workspace/:id" element={<ProtectedRoute module="tasks"><Workspace /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute roles={ROLES_BIZ} module="customers"><Customers /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute roles={ROLES_BIZ} module="products"><Products /></ProtectedRoute>} />
              <Route path="/inventory" element={<ProtectedRoute roles={ROLES_BIZ} module="products"><Inventory /></ProtectedRoute>} />
              <Route path="/material-logs" element={<ProtectedRoute roles={ROLES_BIZ} module="products"><MaterialLogs /></ProtectedRoute>} />
              <Route path="/team-guide" element={<ProtectedRoute><TeamGuide /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute roles={ROLES_BIZ} module="orders"><Orders /></ProtectedRoute>} />
              <Route path="/orders/new" element={<ProtectedRoute roles={ROLES_BIZ} module="orders"><OrderNew /></ProtectedRoute>} />
              <Route path="/orders/:id/edit" element={<ProtectedRoute roles={ROLES_BIZ} module="orders"><OrderNew /></ProtectedRoute>} />
              <Route path="/orders/:id" element={<ProtectedRoute roles={ROLES_BIZ} module="orders"><OrderDetail /></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute roles={ROLES_BIZ} module="expenses"><Expenses /></ProtectedRoute>} />
              <Route path="/expenses/new" element={<ProtectedRoute roles={ROLES_BIZ} module="expenses"><ExpenseNew /></ProtectedRoute>} />
              <Route path="/suppliers" element={<ProtectedRoute roles={ROLES_BIZ} module="suppliers"><Suppliers /></ProtectedRoute>} />
              <Route path="/purchase-orders" element={<ProtectedRoute roles={ROLES_BIZ} module="purchase_orders"><PurchaseOrders /></ProtectedRoute>} />
              <Route path="/purchase-orders/new" element={<ProtectedRoute roles={ROLES_BIZ} module="purchase_orders"><PurchaseOrderNew /></ProtectedRoute>} />
              <Route path="/purchase-orders/:id/edit" element={<ProtectedRoute roles={ROLES_BIZ} module="purchase_orders"><PurchaseOrderNew /></ProtectedRoute>} />
              <Route path="/order-requests" element={<ProtectedRoute roles={ROLES_LEADS} module="shop_requests"><OrderRequests /></ProtectedRoute>} />
              <Route path="/files" element={<ProtectedRoute module="files"><Files /></ProtectedRoute>} />
              <Route path="/mfg" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.dashboard"><MfgDashboard /></ProtectedRoute>} />
              <Route path="/mfg/entry" element={<ProtectedRoute module="mfg.production"><MfgProductionEntry /></ProtectedRoute>} />
              <Route path="/hourly-log" element={<ProtectedRoute module="mfg.hourly_log"><HourlyLog /></ProtectedRoute>} />
              <Route path="/mfg/hourly-log" element={<ProtectedRoute module="mfg.hourly_log"><HourlyLog /></ProtectedRoute>} />
              <Route path="/mfg/entries" element={<ProtectedRoute module="mfg.production"><MfgProductionEntries /></ProtectedRoute>} />
              <Route path="/mfg/planning" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.production"><MfgPlanning /></ProtectedRoute>} />
              <Route path="/mfg/reports" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.reports"><MfgReports /></ProtectedRoute>} />
              <Route path="/mfg/alerts" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.dashboard"><MfgAlerts /></ProtectedRoute>} />
              <Route path="/mfg/employees" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.payroll"><MfgEmployees /></ProtectedRoute>} />
              <Route path="/mfg/attendance" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.shifts"><MfgAttendance /></ProtectedRoute>} />
              <Route path="/mfg/payroll" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.payroll"><MfgPayroll /></ProtectedRoute>} />
              <Route path="/mfg/machines" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.machines"><MfgMachines /></ProtectedRoute>} />
              <Route path="/mfg/moulds" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.moulds"><MfgMoulds /></ProtectedRoute>} />
              <Route path="/mfg/products" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.products"><MfgProducts /></ProtectedRoute>} />
              <Route path="/mfg/shifts" element={<ProtectedRoute roles={ROLES_BIZ} module="mfg.shifts"><MfgShifts /></ProtectedRoute>}  />
              <Route path="/crm" element={<ProtectedRoute roles={ROLES_BIZ} module="crm.dashboard"><CrmDashboard /></ProtectedRoute>} />
              <Route path="/crm/leads" element={<ProtectedRoute roles={ROLES_BIZ} module="crm.leads"><CrmLeads /></ProtectedRoute>} />
              <Route path="/crm/pipeline" element={<ProtectedRoute roles={ROLES_BIZ} module="crm.pipeline"><CrmPipeline /></ProtectedRoute>} />
              <Route path="/crm/activities" element={<ProtectedRoute roles={ROLES_BIZ} module="crm.activities"><CrmActivities /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute roles={ROLES_ADMIN} redirectTo="/dashboard"><Admin /></ProtectedRoute>} />
              <Route path="/admin/performance" element={<ProtectedRoute roles={ROLES_LEADS}><AdminPerformance /></ProtectedRoute>} />
              <Route path="/admin/transporters" element={<ProtectedRoute roles={ROLES_LEADS}><AdminTransporters /></ProtectedRoute>} />
              <Route path="/admin/transporter-ageing" element={<ProtectedRoute roles={ROLES_LEADS}><AdminTransporterAgeing /></ProtectedRoute>} />
              <Route path="/admin/trip-tracker" element={<ProtectedRoute roles={ROLES_LEADS}><AdminTripTracker /></ProtectedRoute>} />
              <Route path="/admin/wallets" element={<ProtectedRoute roles={ROLES_LEADS}><AdminWallets /></ProtectedRoute>} />
              <Route path="/admin/orders" element={<ProtectedRoute roles={ROLES_LEADS}><B2COrders /></ProtectedRoute>} />
              <Route path="/admin/b2c-ledger" element={<ProtectedRoute roles={ROLES_LEADS}><B2CLedger /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute roles={ROLES_LEADS}><AccountingReports /></ProtectedRoute>} />
              <Route path="/admin/reviews" element={<ProtectedRoute roles={ROLES_LEADS}><AdminReviews /></ProtectedRoute>} />
              {/* B2B wholesale catalog — technical details, cart & WhatsApp
                  ordering. Kept at /catalog so retail shoppers on /shop see
                  only the curated combo experience. */}
              <Route path="/catalog" element={<Shop />} />
              {/* B2C retail storefront — Shopify-style combos gallery. */}
              <Route path="/shop" element={<Store />} />
              {/* Public → internal chat bridge (login-gated, resolves & redirects). */}
              <Route path="/chat-support" element={<ChatSupportBridge />} />
              {/* Public branded courier tracking. Anyone with an AWB can view. */}
              <Route path="/track" element={<PublicTrack />} />
              <Route path="/track/:awb" element={<PublicTrack />} />
              {/* Buyer-facing rating page — reached from the "Review Booster" WhatsApp */}
              <Route path="/rate/:order" element={<PublicRate />} />
              {/* Legacy alias — older links to /store keep working. */}
              <Route path="/store" element={<Navigate to="/shop" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
